import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import type { CreateEmployeeRequest, EmployeeResponse, UpdateEmployeeRequest } from '../api/generated';
import { CreateEmployeeRequestRoleEnum, UpdateEmployeeRequestRoleEnum } from '../api/generated';

interface EmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  employee?: EmployeeResponse | null;
  salons: any[];
  onSave: (data: CreateEmployeeRequest | UpdateEmployeeRequest, employeeId?: string) => Promise<void>;
}

const ROLE_LABELS: Record<string, string> = {
  OWNER: 'Vlastník',
  RECEPTION: 'Recepce',
  WORKER: 'Pracovník',
};

const EmployeeModal: React.FC<EmployeeModalProps> = ({ isOpen, onClose, mode, employee, salons, onSave }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [role, setRole] = useState<CreateEmployeeRequestRoleEnum | UpdateEmployeeRequestRoleEnum>(CreateEmployeeRequestRoleEnum.Worker);
  const [salonId, setSalonId] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Reset form when employee changes
  useEffect(() => {
    if (mode === 'edit' && employee) {
      setFirstName(employee.firstName || '');
      setLastName(employee.lastName || '');
      setEmail(employee.email || '');
      setPhoneNumber(employee.phoneNumber || '');
      setRole((employee.role as UpdateEmployeeRequestRoleEnum) || UpdateEmployeeRequestRoleEnum.Worker);
      setSalonId(employee.salonId || '');
    } else if (mode === 'create') {
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhoneNumber('');
      setRole(CreateEmployeeRequestRoleEnum.Worker);
      setSalonId(salons[0]?.id || '');
    }
  }, [mode, employee, isOpen, salons]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (mode === 'create') {
        const payload: CreateEmployeeRequest = {
          firstName,
          lastName,
          email,
          phoneNumber: phoneNumber || undefined,
          role: role as CreateEmployeeRequestRoleEnum,
          salonId: salonId || undefined,
        };
        await onSave(payload);
      } else {
        const payload: UpdateEmployeeRequest = {
          firstName,
          lastName,
          phoneNumber: phoneNumber || undefined,
          role: role as UpdateEmployeeRequestRoleEnum,
        };
        await onSave(payload, employee?.id);
      }
      onClose();
    } catch (error) {
      console.error('Chyba při ukládání zaměstnance:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const isFormValid = () => {
    if (!firstName.trim() || !lastName.trim() || !role) return false;
    if (mode === 'create' && !email.trim()) return false;
    return true;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">{mode === 'create' ? 'Nový zaměstnanec' : 'Upravit zaměstnance'}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jméno *</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Jan"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Příjmení *</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Novák"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email {mode === 'create' && '*'}</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={mode === 'edit'}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
                placeholder="jan.novak@salon.cz"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={e => setPhoneNumber(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="+420 123 456 789"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
              <select
                value={role}
                onChange={e => setRole(e.target.value as CreateEmployeeRequestRoleEnum)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value={CreateEmployeeRequestRoleEnum.Owner}>{ROLE_LABELS.OWNER}</option>
                <option value={CreateEmployeeRequestRoleEnum.Reception}>{ROLE_LABELS.RECEPTION}</option>
                <option value={CreateEmployeeRequestRoleEnum.Worker}>{ROLE_LABELS.WORKER}</option>
              </select>
            </div>

            {mode === 'create' && salons.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Salon</label>
                <select
                  value={salonId}
                  onChange={e => setSalonId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  {salons.map(salon => (
                    <option key={salon.id} value={salon.id}>
                      {salon.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t">
            <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">
              Zrušit
            </button>
            <button
              onClick={handleSave}
              disabled={!isFormValid() || isSaving}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Ukládání...' : mode === 'create' ? 'Vytvořit zaměstnance' : 'Uložit změny'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeModal;
